import { useCallback, useState } from 'react';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { CollectionView } from 'common';

import * as appHelpers from '@src/services/helpers/appHelpers';
import { CollectionsListState } from '@src/types/Collection';
import { getNextCollectionsListState } from './utils/getNextCollectionsListState';

interface UseCollectionDragAndDropArgs {
  collections: CollectionView[];
  collectionsList: CollectionsListState;
  setCollectionsList: React.Dispatch<React.SetStateAction<CollectionsListState>>;
  findCategoryForCollection: (collectionId: string) => string | undefined;
  onPersistCategoryChange: (collection: CollectionView, destinationCategory: string) => void;
}

export function useCollectionDragAndDrop({
  collections,
  collectionsList,
  setCollectionsList,
  findCategoryForCollection,
  onPersistCategoryChange,
}: UseCollectionDragAndDropArgs) {
  const [isDragging, setIsDragging] = useState(false);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (event.active.data.current?.type !== 'collection-card') {
      return;
    }
    setActiveCollectionId(event.active.id as string);
    setIsDragging(true);
  }, []);

  const handleDragCancel = useCallback(() => {
    setIsDragging(false);
    setActiveCollectionId(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setIsDragging(false);
      setActiveCollectionId(null);
      if (!over || active.data.current?.type !== 'collection-card') {
        return;
      }
      const activeId = active.id as string;
      const movingCollection = collections.find((c) => c._id === activeId);
      if (!movingCollection) {
        return;
      }
      const sourceCategory = findCategoryForCollection(activeId);
      if (!sourceCategory) {
        return;
      }
      const overType = over.data.current?.type as string | undefined;
      const overId = over.id as string;
      const destinationCategory =
        overType === 'category-container'
          ? (over.data.current?.category as string)
          : findCategoryForCollection(overId);
      if (!destinationCategory) {
        return;
      }
      if (movingCollection.locked) {
        appHelpers.addAlert({ text: 'Collection is locked!', type: 'warning' });
        return;
      }
      const canEditCollection = Boolean(movingCollection.i_can_edit || movingCollection.mine);
      if (!canEditCollection) {
        appHelpers.addAlert({ text: 'Need edit permissions!', type: 'warning' });
        return;
      }
      if (sourceCategory === destinationCategory && overId === activeId) {
        return;
      }
      const { nextList, didUpdate } = getNextCollectionsListState({
        currentList: collectionsList,
        activeId,
        overId,
        overType,
        sourceCategory,
        destinationCategory,
      });

      if (!didUpdate) {
        return;
      }

      setCollectionsList(nextList);
      onPersistCategoryChange(movingCollection, destinationCategory);
    },
    [collections, collectionsList, findCategoryForCollection, onPersistCategoryChange, setCollectionsList]
  );

  return { isDragging, activeCollectionId, handleDragStart, handleDragEnd, handleDragCancel };
}
